import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { getPoemById, fetchPoemContent, getAdjacentPoems } from '../../services/poetryService';
import { useReadingHistory } from '../../contexts/ReadingHistoryContext';
import { useTheme } from '../../contexts/ThemeContext';
import { usePageNavigation } from '../../hooks/useNavigation';

interface PoemReaderProps {
  onZenModeChange: (isZen: boolean) => void;
}

interface PoemSection {
  title: string;
  lines: string;
  prefixNotes: string[];
  suffixNotes: string[];
}

interface ParsedContent {
  poemLines: string;
  prefixNotes: string[];
  suffixNotes: string[];
  sections: PoemSection[] | null;
}

// Parse content into poem lines, note lines (starting with ###) and optional
// sections (delimited by ## headings). Returns sections=null when no ## found.
// Content before the first ## becomes an implicit "Увод" section.
// When sections exist, ### notes are scoped to their section, not shown globally.
const parseContent = (content: string): ParsedContent => {
  const lines = content.split('\n');
  const prefixNotes: string[] = [];
  const suffixNotes: string[] = [];
  const poemLines: string[] = [];

  let foundPoemContent = false;
  let hasSectionMarkers = false;

  // First pass: detect ## markers
  for (const line of lines) {
    if (line.startsWith('##') && !line.startsWith('###')) {
      hasSectionMarkers = true;
      break;
    }
  }

  // Second pass: build notes + flat poemLines (backwards-compat for non-sectioned poems)
  for (const line of lines) {
    if (line.startsWith('###')) {
      const note = line.slice(3).trim();
      if (!foundPoemContent) {
        prefixNotes.push(note);
      } else {
        suffixNotes.push(note);
      }
    } else {
      if (line.trim() !== '' || foundPoemContent) {
        foundPoemContent = true;
      }
      poemLines.push(line);
    }
  }

  const flatPoemLines = poemLines.join('\n');

  if (!hasSectionMarkers) {
    return { poemLines: flatPoemLines, prefixNotes, suffixNotes, sections: null };
  }

  // Build sections directly from raw lines so ### notes are scoped per section.
  // Each section accumulates its own prefix/suffix notes and poem lines.
  const sections: PoemSection[] = [];
  let currentTitle = 'Увод';
  let currentPoemLines: string[] = [];
  let currentPrefixNotes: string[] = [];
  let currentSuffixNotes: string[] = [];
  let sectionHasPoemContent = false;

  const flushSection = () => {
    const trimmed = currentPoemLines.join('\n').trim();
    if (trimmed.length > 0 || currentPrefixNotes.length > 0) {
      sections.push({
        title: currentTitle,
        lines: trimmed,
        prefixNotes: currentPrefixNotes,
        suffixNotes: currentSuffixNotes,
      });
    }
  };

  for (const line of lines) {
    if (line.startsWith('##') && !line.startsWith('###')) {
      // New section heading — flush current section first
      flushSection();
      currentTitle = line.slice(2).trim();
      currentPoemLines = [];
      currentPrefixNotes = [];
      currentSuffixNotes = [];
      sectionHasPoemContent = false;
    } else if (line.startsWith('###')) {
      const note = line.slice(3).trim();
      if (!sectionHasPoemContent) {
        currentPrefixNotes.push(note);
      } else {
        currentSuffixNotes.push(note);
      }
    } else {
      if (line.trim() !== '') {
        sectionHasPoemContent = true;
      }
      currentPoemLines.push(line);
    }
  }
  flushSection();

  return {
    poemLines: flatPoemLines,
    prefixNotes,
    suffixNotes,
    sections: sections.length > 0 ? sections : null,
  };
};

const PoemReader: React.FC<PoemReaderProps> = ({ onZenModeChange }) => {
  const { poemId } = useParams<{ poemId: string }>();
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isZenMode, setIsZenMode] = useState(false);
  const [contentPadding, setContentPadding] = useState<number | null>(null);
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const { addToHistory } = useReadingHistory();
  const { fontSize } = useTheme();
  const contentRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLSpanElement>(null);
  const prevFontSizeRef = useRef(fontSize);

  // Use ref to avoid re-triggering effect when addToHistory changes
  const addToHistoryRef = useRef(addToHistory);
  addToHistoryRef.current = addToHistory;

  // Memoize result to prevent recalculation on every render
  const result = useMemo(() => poemId ? getPoemById(poemId) : null, [poemId]);

  // Get adjacent poems for navigation
  const { prevPoem, nextPoem } = useMemo(() => poemId ? getAdjacentPoems(poemId) : { prevPoem: null, nextPoem: null }, [poemId]);

  // Parse content into poem lines, notes and optional sections
  const { poemLines, prefixNotes, suffixNotes, sections } = useMemo(() => parseContent(content), [content]);

  // Reset section index whenever the poem changes
  useEffect(() => {
    setCurrentSectionIndex(0);
  }, [poemId]);

  // Scroll to top on poem or section change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [poemId, currentSectionIndex]);

  // Active content: current section lines, or full poem if no sections
  const activeContent = sections ? sections[currentSectionIndex].lines : poemLines;

  // Active notes: scoped to current section when sections exist
  const activePrefixNotes = sections ? sections[currentSectionIndex].prefixNotes : prefixNotes;
  const activeSuffixNotes = sections ? sections[currentSectionIndex].suffixNotes : suffixNotes;

  // Section navigation callbacks (null at boundaries → falls through to poem nav)
  const onPrevSection = sections && currentSectionIndex > 0
    ? () => setCurrentSectionIndex(i => i - 1)
    : null;
  const onNextSection = sections && currentSectionIndex < sections.length - 1
    ? () => setCurrentSectionIndex(i => i + 1)
    : null;

  // Keyboard and swipe navigation (sections take priority over poem navigation)
  const { onTouchStart, onTouchEnd } = usePageNavigation(prevPoem, nextPoem, { onPrevSection, onNextSection });

  // Calculate centering based on longest verse (excluding notes)
  useEffect(() => {
    if (!activeContent || !contentRef.current || !measureRef.current) return;

    // Check if this is a font size change (not initial load)
    const isFontSizeChange = prevFontSizeRef.current !== fontSize && contentPadding !== null;
    prevFontSizeRef.current = fontSize;

    // Hide content while recalculating
    if (isFontSizeChange) {
      setIsRecalculating(true);
    }

    const calculateCentering = () => {
      const containerWidth = contentRef.current?.parentElement?.clientWidth || 0;
      if (containerWidth === 0) return;

      // Get computed styles for accurate measurement
      const computedStyle = window.getComputedStyle(contentRef.current!);
      const currentFontSize = computedStyle.fontSize;
      const fontFamily = computedStyle.fontFamily;

      // Set up measurement span with same styles
      measureRef.current!.style.fontSize = currentFontSize;
      measureRef.current!.style.fontFamily = fontFamily;
      measureRef.current!.style.whiteSpace = 'pre';
      measureRef.current!.style.visibility = 'hidden';
      measureRef.current!.style.position = 'absolute';

      // Find the longest line (only poem lines, not notes)
      const lines = activeContent.split('\n');
      let maxWidth = 0;

      for (const line of lines) {
        measureRef.current!.textContent = line;
        const lineWidth = measureRef.current!.getBoundingClientRect().width;
        if (lineWidth > maxWidth) {
          maxWidth = lineWidth;
        }
      }

      // Calculate padding to center the longest line
      // Only apply if longest line fits with some margin
      const minPadding = 16; // minimum padding on each side
      const availableWidth = containerWidth - (minPadding * 2);

      if (maxWidth > 0 && maxWidth <= availableWidth) {
        const padding = Math.floor((containerWidth - maxWidth) / 2);
        setContentPadding(Math.max(padding, minPadding));
      } else {
        setContentPadding(minPadding);
      }
      setIsRecalculating(false);
    };

    // Small delay to ensure fonts are loaded and styles applied
    const timer = setTimeout(calculateCentering, 100);

    // Recalculate on window resize
    window.addEventListener('resize', calculateCentering);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', calculateCentering);
    };
  // contentPadding intentionally omitted — including it would cause an infinite loop
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeContent, fontSize]);

  useEffect(() => {
    if (!poemId || !result) return;

    let isMounted = true;

    const loadPoem = async () => {
      setLoading(true);
      setError(null);
      setContentPadding(null);
      try {
        const text = await fetchPoemContent(poemId);
        if (isMounted) {
          setContent(text);

          // Add to reading history
          addToHistoryRef.current({
            poemId,
            poemName: result.poem.name,
            poetName: result.poet.name,
            catalogId: result.catalog.id,
            timestamp: Date.now()
          });
        }
      } catch (e) {
        if (isMounted) {
          setError('Није могуће учитати песму. Покушајте поново.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadPoem();

    return () => {
      isMounted = false;
    };
  }, [poemId, result]);

  const toggleZenMode = useCallback(() => {
    setIsZenMode(prev => {
      const newValue = !prev;
      onZenModeChange(newValue);
      return newValue;
    });
  }, [onZenModeChange]);

  if (!poemId || !result) {
    return <Navigate to="/" replace />;
  }

  const { poem, poet, source } = result;

  const contentStyle = contentPadding !== null ? {
    paddingLeft: `${contentPadding}px`,
    paddingRight: `${contentPadding}px`,
    opacity: isRecalculating ? 0 : 1
  } : {
    opacity: 0
  };

  return (
    <div className={`poem-reader ${isZenMode ? 'poem-reader--zen-mode' : ''} ${sections ? 'poem-reader--has-sections' : ''}`} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      {/* Hidden element for measuring text width */}
      <span ref={measureRef} aria-hidden="true" />

      <div className="poem-reader__header">
        <div className="poem-reader__title-bar">
          {prevPoem ? (
            <Link
              to={prevPoem.id.startsWith('bio:') ? `/bio/${prevPoem.id.slice(4)}` : `/poem/${prevPoem.id}`}
              className="poem-reader__nav poem-reader__nav--prev"
              aria-label={prevPoem.id.startsWith('bio:') ? 'О песнику' : 'Претходна песма'}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
                <polyline points="15 18 9 12 15 6"/>
              </svg>
            </Link>
          ) : (
            <div className="poem-reader__nav poem-reader__nav--placeholder" />
          )}

          <div className="poem-reader__title-content">
            <div className="poem-reader__poet">{poet.name}</div>
            <div className="poem-reader__poem-title">{poem.name}</div>
          </div>

          {nextPoem ? (
            <Link to={`/poem/${nextPoem.id}`} className="poem-reader__nav poem-reader__nav--next" aria-label="Следећа песма">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
                <polyline points="9 18 15 12 9 6"/>
              </svg>
            </Link>
          ) : (
            <div className="poem-reader__nav poem-reader__nav--placeholder" />
          )}
        </div>

        {sections && (
          <div className="poem-reader__section-bar">
            <button
              className="poem-reader__section-nav poem-reader__section-nav--prev"
              onClick={onPrevSection ?? undefined}
              disabled={!onPrevSection}
              aria-label="Претходни одељак"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
                <polyline points="15 18 9 12 15 6"/>
              </svg>
            </button>

            <span className="poem-reader__section-title">
              {sections[currentSectionIndex].title}
            </span>

            <button
              className="poem-reader__section-nav poem-reader__section-nav--next"
              onClick={onNextSection ?? undefined}
              disabled={!onNextSection}
              aria-label="Следећи одељак"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
                <polyline points="9 18 15 12 9 6"/>
              </svg>
            </button>
          </div>
        )}
      </div>

      {loading && (
        <div className="poem-reader__loading">Учитавање...</div>
      )}

      {error && (
        <div className="poem-reader__error">{error}</div>
      )}

      {!loading && !error && (
        <>
          {activePrefixNotes.length > 0 && (
            <div className="poem-reader__notes poem-reader__notes--prefix">
              {activePrefixNotes.map((note, index) => (
                <div key={index} className="poem-reader__note">{note}</div>
              ))}
            </div>
          )}
          <div
            ref={contentRef}
            className="poem-reader__content"
            style={contentStyle}
            onClick={toggleZenMode}
            onKeyDown={(e) => e.key === 'Enter' && toggleZenMode()}
            tabIndex={0}
            role="button"
            aria-label={isZenMode ? 'Изађи из режима читања' : 'Уђи у режим читања'}
          >
            {activeContent}
          </div>
          {activeSuffixNotes.length > 0 && (
            <div className="poem-reader__notes poem-reader__notes--suffix">
              {activeSuffixNotes.map((note, index) => (
                <div key={index} className="poem-reader__note">{note}</div>
              ))}
            </div>
          )}
          {source && (
            <div className="poem-reader__source">
              Извор: {source}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PoemReader;

